import { useState } from 'react';
import { Link } from 'react-router-dom';

const WealthIntelligencePage = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  
  // What You'll Learn columns
  const learningColumns = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: 'The Stewardship Mindset',
      description: 'How to see your money as a tool for purpose and legacy, not just survival.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'How Markets Work',
      description: 'Plain-language breakdowns of stocks, ETFs, index funds, and how to put your first dollar to work.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: 'Compound Growth',
      description: "The math behind building generational wealth — and why starting with $5 changes everything."
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0d0d0d', fontFamily: "'Inter', sans-serif" }}>
      {/* Google Fonts */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" 
        rel="stylesheet" 
      />

      {/* Sticky Nav */}
      <nav 
        className="sticky top-0 z-50 px-6 py-4 flex items-center justify-between"
        style={{ backgroundColor: '#111111' }}
      >
        <span 
          className="text-xl font-bold tracking-wide"
          style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
        >
          POWR
        </span>
        <Link 
          to="/courses"
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: '#c9a84c' }}
        >
          ← Back to Course Hub
        </Link>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-20 text-center max-w-4xl mx-auto">
        <p 
          className="text-xs tracking-[0.2em] uppercase mb-6"
          style={{ color: '#c9a84c' }}
        >
          POWR COMMUNITY · INVESTMENT TOOLS
        </p>
        <h1 
          className="text-4xl md:text-5xl lg:text-6xl mb-6"
          style={{ 
            color: '#ffffff', 
            fontFamily: "'Playfair Display', serif",
            fontWeight: 500,
            lineHeight: 1.2
          }}
        >
          Wealth Intelligence
        </h1>
        <p 
          className="text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
          style={{ color: '#e5e5e5' }}
        >
          AI-powered investment education built for Black men who are serious about building legacy wealth.
        </p>
        <a
          href="https://www.perplexity.ai/computer/a/powr-wealth-intelligence-ceOT2_wJTKC6KN.AlS679g"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block font-bold transition-all hover:scale-105"
          style={{ 
            backgroundColor: '#c9a84c',
            color: '#000000',
            fontWeight: 700,
            borderRadius: '4px',
            padding: '14px 32px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#e2c97e'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#c9a84c'}
        >
          Launch the Learning Tool
        </a>
        <p 
          className="text-xs mt-4 opacity-70"
          style={{ color: '#e5e5e5' }}
        >
          Free to access · Educational only · No account required
        </p>
      </section>

      {/* Iframe Embed */}
      <section className="w-full" style={{ height: '100vh', padding: 0 }}>
        {!iframeLoaded && (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: '#0d0d0d' }}
          >
            <div className="text-center">
              <div 
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }}
              />
              <p style={{ color: '#e5e5e5' }}>Loading Wealth Intelligence...</p>
            </div>
          </div>
        )}
        <iframe
          src="https://www.perplexity.ai/computer/a/powr-wealth-intelligence-ceOT2_wJTKC6KN.AlS679g"
          title="POWR Wealth Intelligence"
          className="w-full h-full border-0"
          style={{ 
            display: iframeLoaded ? 'block' : 'none',
            overflow: 'hidden'
          }}
          onLoad={() => setIframeLoaded(true)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </section>

      {/* About This Tool */}
      <section 
        className="py-16 px-6"
        style={{ backgroundColor: '#111111' }}
      >
        <div className="max-w-5xl mx-auto">
          <h2 
            className="text-2xl md:text-3xl font-bold text-center mb-12"
            style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
          >
            What You'll Learn
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {learningColumns.map((column, index) => (
              <div key={index} className="text-center">
                <div 
                  className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full"
                  style={{ backgroundColor: 'rgba(201, 168, 76, 0.15)', color: '#c9a84c' }}
                >
                  {column.icon}
                </div>
                <h3 
                  className="text-lg font-bold mb-3"
                  style={{ color: '#ffffff' }}
                >
                  {column.title}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: '#e5e5e5' }}
                >
                  {column.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 text-center">
        <p 
          className="text-xs max-w-3xl mx-auto leading-relaxed"
          style={{ color: '#666666' }}
        >
          This tool is for educational purposes only. It does not constitute financial advice. Always consult a licensed financial advisor before making investment decisions.
        </p>
      </section>

      {/* Footer */}
      <footer 
        className="py-12 px-6 text-center"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        <p 
          className="text-2xl font-bold mb-3"
          style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}
        >
          POWR
        </p>
        <p 
          className="text-sm mb-4"
          style={{ color: '#e5e5e5' }}
        >
          Purpose · Wealth · Legacy
        </p>
        <p 
          className="text-xs"
          style={{ color: '#666666' }}
        >
          © {new Date().getFullYear()} POWR. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default WealthIntelligencePage;
